import { useCallback, useEffect, useRef, useState } from 'react';

import { AgentRequestError, streamAgentTurn } from './agentClient';
import type { AgentTokenProvider } from './agentClient';
import { AgentDelegationError } from './delegation';
import {
  AGENT_CHAT_ITEM,
  appendUserMessage,
  applyStreamEvent,
  createChatItemIdFactory,
  finalizeTranscript,
} from './chatItems';
import type { AgentChatItem } from './chatItems';

type UseAgentChatStreamOptions = {
  /** Client-generated external thread id the turn is posted to. */
  threadId: string;
  getToken: AgentTokenProvider;
  /** Injectable stream client for focused tests. */
  streamTurn?: typeof streamAgentTurn;
  /** Invoked after a turn ends so callers can refresh the thread list and persist successful threads. */
  onTurnSettled?: (didPersistTurn: boolean) => void;
};

/** Streaming turn state and controls returned by {@link useAgentChatStream}. */
export type UseAgentChatStreamResult = {
  /** Transcript items produced in this session for the current thread. */
  items: readonly AgentChatItem[];
  /** True from the moment a turn is posted until its stream closes or is aborted. */
  isStreaming: boolean;
  /** Posts a message and streams the turn; blank input and concurrent sends are ignored. */
  sendMessage: (message: string) => Promise<void>;
  /** Aborts the in-flight turn, keeping whatever was already streamed. */
  stopStreaming: () => void;
  /** Aborts any in-flight turn and clears session transcript state (used when switching threads). */
  resetTranscript: () => void;
};

const isAbortError = (error: Error): boolean => error.name === 'AbortError';

/**
 * Converts a thrown transport error into a transcript row.
 *
 * A delegation error may carry separately marked GraphQL-localized copy. Client invariant failures
 * and `AgentRequestError` messages are untranslated operator text, so the row keeps just the code
 * and the panel localizes it.
 */
const toErrorItem = (error: Error, id: string): AgentChatItem => {
  if (error instanceof AgentDelegationError) {
    return {
      type: AGENT_CHAT_ITEM.TURN_ERROR,
      id,
      code: error.code,
      localizedMessage: error.localizedMessage,
    };
  }
  return {
    type: AGENT_CHAT_ITEM.TURN_ERROR,
    id,
    code: error instanceof AgentRequestError ? error.code : 'INTERNAL',
    localizedMessage: null,
  };
};

/**
 * Streams one agent turn at a time for a thread, owning transcript state for the current session.
 *
 * Transport is raw `fetch` + `ReadableStream` (see `agentClient`), so cancellation is an
 * `AbortController` the caller drives through `stopStreaming` or `resetTranscript`.
 */
export default function useAgentChatStream({
  threadId,
  getToken,
  onTurnSettled,
  streamTurn = streamAgentTurn,
}: UseAgentChatStreamOptions): UseAgentChatStreamResult {
  const [items, setItems] = useState<readonly AgentChatItem[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const nextIdRef = useRef(createChatItemIdFactory());

  // This hook is owned by AgentChatSessionProvider, rather than an individual visual surface, so
  // only dashboard-session teardown unmounts it. Explicit cancel and thread switching use the
  // controls below; clearing abortRef also makes the pending sendMessage guard skip post-unmount
  // state updates.
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
      abortRef.current = null;
    };
  }, []);

  const sendMessage = useCallback(
    async (rawMessage: string) => {
      const message = rawMessage.trim();
      // A thread allows one active turn server-side; ignore sends while a turn is in flight.
      if (message.length === 0 || abortRef.current) {
        return;
      }

      const nextId = nextIdRef.current;
      const controller = new AbortController();
      abortRef.current = controller;
      setIsStreaming(true);
      setItems((previous) => appendUserMessage(previous, message, nextId));
      let didPersistTurn = false;

      try {
        await streamTurn({ threadId, message, signal: controller.signal }, getToken, (event) => {
          if (abortRef.current !== controller) {
            return;
          }
          setItems((previous) => applyStreamEvent(previous, event, nextId));
        });
        didPersistTurn = true;
      } catch (cause) {
        const error = cause instanceof Error ? cause : new Error('Agent stream failed with a non-error value.');
        if (!isAbortError(error) && abortRef.current === controller) {
          setItems((previous) => [...finalizeTranscript(previous), toErrorItem(error, nextId('ERROR'))]);
        }
      } finally {
        // A turn abandoned by `resetTranscript` must not clobber the state of the turn that replaced it.
        if (abortRef.current === controller) {
          abortRef.current = null;
          setIsStreaming(false);
          setItems(finalizeTranscript);
          onTurnSettled?.(didPersistTurn);
        }
      }
    },
    [threadId, getToken, onTurnSettled, streamTurn]
  );

  const stopStreaming = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const resetTranscript = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    nextIdRef.current = createChatItemIdFactory();
    setIsStreaming(false);
    setItems([]);
  }, []);

  return { items, isStreaming, sendMessage, stopStreaming, resetTranscript };
}
